import { History, Pencil, Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { LoadingRows } from "@/components/shared/loading-rows";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  type HistoryAttribute,
  type ManagerHistorySpan,
  useAddSpan,
  useDeleteSpan,
  usePersonHistory,
  useUpdateSpan,
} from "@/hooks/use-persons";
import { useRoleClassificationOptions } from "@/hooks/use-role-classifications";
import { formatDateOnly } from "@/lib/dates";

/**
 * The clock icon that sits beside Manager on the profile, and the span list it
 * opens.
 *
 * Both live here so the profile row stays a one-liner, and so the fetch is
 * gated on the dialog actually being open — the route is admin-or-self, and most
 * viewers never open it.
 */
/**
 * Wording per series. Kept here rather than passed in so every caller gets the
 * same phrasing, and so a new attribute has one obvious place to describe itself.
 */
/** `emptyValue` is what a null reads as — "Nobody" is wrong for a classification. */
const COPY: Record<
  HistoryAttribute,
  { title: string; valueLabel: string; emptyValue: string; blurb: (name: string) => string }
> = {
  manager: {
    title: "Manager history",
    valueLabel: "Manager",
    emptyValue: "Nobody",
    blurb: (name) =>
      `Who managed ${name}, and when. Seeded from NOS, then maintained automatically as the manager changes.`,
  },
  dotted_line: {
    title: "Dotted line history",
    valueLabel: "Dotted line",
    emptyValue: "Nobody",
    blurb: (name) =>
      `${name}'s dotted lines over time. Recorded in Enable only — NOS has no mentor history to seed from.`,
  },
  role_classification: {
    title: "Role classification history",
    valueLabel: "Role classification",
    emptyValue: "None",
    blurb: (name) =>
      `${name}'s role classification over time. NOS has a table for this but has never filled it, so Enable's record starts from the first change made here.`,
  },
};

/** Radix Select has no empty-string value, so null needs a sentinel. */
const NONE = "__none__";

export function PersonHistoryButton({
  personId,
  personName,
  attribute,
  isAdmin,
  open,
  onOpenChange,
}: {
  personId: string;
  personName: string;
  attribute: HistoryAttribute;
  /** Edit tier — admins can correct spans and add history NOS never had. */
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <>
      <SimpleTooltip content={COPY[attribute].title}>
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-label={`${COPY[attribute].title} for ${personName}`}
          className="inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <History className="size-3.5" />
        </button>
      </SimpleTooltip>
      <PersonHistoryDialog
        personId={personId}
        personName={personName}
        attribute={attribute}
        isAdmin={isAdmin}
        open={open}
        onOpenChange={onOpenChange}
      />
    </>
  );
}

function PersonHistoryDialog({
  personId,
  personName,
  attribute,
  isAdmin,
  open,
  onOpenChange,
}: {
  personId: string;
  personName: string;
  attribute: HistoryAttribute;
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, refetch } = usePersonHistory(personId, attribute, open);
  const spans = data ?? [];
  // `null` = the add form; a span id = editing that row; undefined = closed.
  const [editing, setEditing] = useState<{ span: ManagerHistorySpan | null } | undefined>();
  const [confirmDelete, setConfirmDelete] = useState<ManagerHistorySpan | undefined>();
  const removeSpan = useDeleteSpan(personId, attribute);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{COPY[attribute].title}</DialogTitle>
          <DialogDescription>{COPY[attribute].blurb(personName)}</DialogDescription>
        </DialogHeader>

        {isLoading && <LoadingRows />}

        {isError && (
          <div className="py-6 text-center">
            <p className="text-muted-foreground text-sm">Couldn't load the history.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 font-medium text-primary text-sm underline"
            >
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && spans.length === 0 && (
          <p className="py-6 text-center text-muted-foreground text-sm">Nothing recorded yet.</p>
        )}

        {spans.length > 0 && (
          <ol className="max-h-[60vh] divide-y overflow-y-auto">
            {spans.map((span) => (
              <li key={span.id} className="flex items-start gap-3 py-2.5">
                <span
                  className={
                    // The open span is the current one — the only row a reader
                    // scanning for "who manages them now" cares about.
                    span.endDate === null
                      ? "mt-1.5 size-2 shrink-0 rounded-full bg-primary"
                      : "mt-1.5 size-2 shrink-0 rounded-full bg-muted-foreground/30"
                  }
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-sm">
                      {span.valueName ?? COPY[attribute].emptyValue}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {formatDateOnly(span.startDate)} —{" "}
                      {span.endDate === null ? "now" : formatDateOnly(span.endDate)}
                    </span>
                    {span.endDate !== null && span.endDate < span.startDate && (
                      // Imported from NOS as-is rather than silently repaired,
                      // so say so instead of rendering a nonsense range quietly.
                      <span className="rounded-full bg-warning-bg px-2 py-0.5 font-medium text-2xs text-warning-foreground">
                        Dates look wrong
                      </span>
                    )}
                  </div>
                  {span.changeReasonName && (
                    <p className="text-muted-foreground text-xs">{span.changeReasonName}</p>
                  )}
                  {span.note && <p className="text-xs">{span.note}</p>}
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditing({ span })}
                      aria-label="Edit this span"
                      className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      <Pencil className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(span)}
                      aria-label="Delete this span"
                      className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}

        {isAdmin &&
          (editing ? (
            <PersonSpanForm
              personId={personId}
              attribute={attribute}
              span={editing.span}
              onDone={() => setEditing(undefined)}
            />
          ) : (
            <div className="flex justify-start border-t pt-3">
              <Button variant="outline" size="sm" onClick={() => setEditing({ span: null })}>
                <Plus className="mr-1.5 size-3.5" />
                Add a span
              </Button>
            </div>
          ))}

        <ConfirmDialog
          open={confirmDelete !== undefined}
          onOpenChange={(o) => {
            if (!o) setConfirmDelete(undefined);
          }}
          title="Delete this span?"
          description={
            confirmDelete
              ? `${confirmDelete.valueName ?? COPY[attribute].emptyValue}, ${formatDateOnly(confirmDelete.startDate)} — ${
                  confirmDelete.endDate === null ? "now" : formatDateOnly(confirmDelete.endDate)
                }. This removes the span from the history; it does not change who manages ${personName} today.`
              : null
          }
          confirmLabel="Delete"
          onConfirm={() => {
            if (confirmDelete) {
              removeSpan.mutate(
                { spanId: confirmDelete.id },
                { onSuccess: () => setConfirmDelete(undefined) },
              );
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add or correct one span.
 *
 * Inline rather than a nested dialog: stacking a modal on a modal to edit a row
 * in the list behind it is disorienting, and the form is four fields.
 *
 * "Current span" is a checkbox rather than an empty end-date field, because
 * "leave this blank to mean it is ongoing" is not discoverable — and the service
 * rejects a second open span, so the reader needs to understand what they are
 * asserting.
 */
function PersonSpanForm({
  personId,
  attribute,
  span,
  onDone,
}: {
  personId: string;
  attribute: HistoryAttribute;
  /** null = adding a new span. */
  span: ManagerHistorySpan | null;
  onDone: () => void;
}) {
  const [value, setValue] = useState<string | null>(span?.value ?? null);
  const [startDate, setStartDate] = useState(span?.startDate ?? "");
  const [isCurrent, setIsCurrent] = useState(span ? span.endDate === null : false);
  const [endDate, setEndDate] = useState(span?.endDate ?? "");
  const [note, setNote] = useState(span?.note ?? "");
  // Unique per mounted form so the label/input pairs stay associated when the
  // add form and an edit form are rendered at different times.
  const formId = useId();

  const add = useAddSpan(personId, attribute);
  const update = useUpdateSpan(personId, attribute);
  // Active classifications only, plus this span's own value when it names one
  // that has since been retired — a correction to an old span must not silently
  // rewrite what it says.
  const classifications = useRoleClassificationOptions();
  const classificationOptions =
    !value || classifications.options.some((o) => o.value === value)
      ? classifications.options
      : [...classifications.options, { value, label: classifications.labelFor(value) }];
  const pending = add.isPending || update.isPending;

  function submit() {
    const input = {
      value,
      startDate,
      endDate: isCurrent ? null : endDate,
      note: note.trim() === "" ? null : note.trim(),
    };
    if (span) {
      update.mutate({ spanId: span.id, input }, { onSuccess: onDone });
    } else {
      add.mutate(input, { onSuccess: onDone });
    }
  }

  // Start date is required, and an end date is required unless this is the
  // current span — otherwise the server would reject and the admin would have to
  // guess which field was wrong.
  const incomplete = startDate === "" || (!isCurrent && endDate === "");

  return (
    <div className="space-y-3 border-t pt-3">
      <p className="font-medium text-sm">{span ? "Edit span" : "Add a span"}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Plain divs, not <label>: EntityCombobox and Checkbox render buttons,
            which a <label> cannot be associated with — the inputs below use
            htmlFor/id instead. */}
        <div className="space-y-1">
          <span className="text-muted-foreground text-xs">{COPY[attribute].valueLabel}</span>
          {/* Role classification is a short admin-managed list, so a plain
              Select — the entity-combobox rule is for long tables, and routing
              a handful of tokens through a search endpoint would be worse UX. */}
          {attribute === "role_classification" ? (
            <Select value={value ?? NONE} onValueChange={(v) => setValue(v === NONE ? null : v)}>
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {classificationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <EntityCombobox
              entity="persons"
              value={value}
              onChange={setValue}
              includeNone
              placeholder="Nobody"
              searchPlaceholder="Search people…"
            />
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-note`} className="text-muted-foreground text-xs">
            Note (optional)
          </Label>
          <Input
            id={`${formId}-note`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-start`} className="text-muted-foreground text-xs">
            Start date
          </Label>
          <Input
            id={`${formId}-start`}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-end`} className="text-muted-foreground text-xs">
            End date
          </Label>
          <Input
            id={`${formId}-end`}
            type="date"
            value={endDate}
            disabled={isCurrent}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Checkbox
          id={`${formId}-current`}
          aria-label="This is the current span (no end date)"
          checked={isCurrent}
          onCheckedChange={(v) => {
            setIsCurrent(v === true);
            if (v === true) setEndDate("");
          }}
        />
        <Label htmlFor={`${formId}-current`} className="font-normal">
          This is the current span (no end date)
        </Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={pending || incomplete}>
          {span ? "Save" : "Add"}
        </Button>
      </div>
    </div>
  );
}
